import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { Fragment } from 'react';
import { Button, Form, InputGroup, Tab, Tabs } from 'react-bootstrap';
import Select from 'react-select';

/* 
  NOTE:
  Whenever using this form component don't wrap it in a react-bootstrap Card component,
  instead use a div with the bootstrap classes.

  EXPLAINATION:
  Whenever you use a Nav element inside of a Card it adds the relevant card-header-x class.
  This breaks the expected look of tabs inside a Card.Body. The work around is to use
  bootstrap classes rather than react-bootstrap components.
  https://github.com/react-bootstrap/react-bootstrap/issues/4771 
*/
function PropertyForm({
  handleSubmit,
  handleChange,
  handleBlur,
  values,
  errors,
  setFieldValue
}) {
  return (
    <Form onSubmit={handleSubmit}>
      {/* Identifier */}
      <Form.Group controlId='identifier'>
        <Form.Label>Identifier</Form.Label>
        <Form.Control
          type='text'
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.identifier}
          placeholder='Property Identifier'
          isInvalid={errors.identifier}
        />
        <Form.Control.Feedback type='invalid'>
          {errors.identifier}
        </Form.Control.Feedback>
      </Form.Group>

      {/* Name */}
      <Form.Group controlId='name'>
        <Form.Label>Name</Form.Label>
        <Form.Control
          type='text'
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.name}
          placeholder='Property Name'
          isInvalid={errors.name}
        />
        <Form.Control.Feedback type='invalid'>
          {errors.name}
        </Form.Control.Feedback>
      </Form.Group>

      {/* Region */}
      <Form.Group controlId='region_id'>
        <Form.Label>Region</Form.Label>
        <Select
          defaultValue={values.region}
          onChange={(option) => setFieldValue('region', option)}
          options={values.regions.map((region) => {
            return {
              value: region.id,
              label: region.name
            };
          })}
        />
        {errors.region_id ? (
          <div className='text-danger'>{errors.region_id}</div>
        ) : null}
      </Form.Group>

      {/* KeyContact */}
      {values.add_key_contact ? (
        <Fragment>
          <hr />
          <h5>
            New Property Key Contact
            <Button
              size='sm'
              variant='outline-danger'
              className='float-right'
              type='button'
              onClick={() => {
                setFieldValue('new_property_key_contact.name', '');
                setFieldValue('new_property_key_contact.email', '');
                setFieldValue('new_property_key_contact.phone_number', '');
                setFieldValue('new_property_key_contact.user', '');
                setFieldValue('add_key_contact', false);
              }}
            >
              Cancel
            </Button>
          </h5>
          <Tabs
            activeKey={values.add_key_contact_tab}
            className='mt-3 mb-2'
            onSelect={(k) => {
              switch (k) {
                case 'user':
                  setFieldValue('new_property_key_contact.name', '');
                  setFieldValue('new_property_key_contact.email', '');
                  setFieldValue('new_property_key_contact.phone_number', '');
                  break;
                case 'details':
                  setFieldValue('new_property_key_contact.user', '');
                  break;
              }
              setFieldValue('add_key_contact_tab', k);
            }}
          >
            <Tab eventKey='user' title='User' disabled>
              <Form.Group>
                <Form.Label>User</Form.Label>
                <Select
                  onChange={(option) =>
                    setFieldValue('new_property_key_contact.user', option)
                  }
                  options={values.users.map((user) => {
                    return {
                      value: user.id,
                      label: user.name
                    };
                  })}
                />
              </Form.Group>
            </Tab>
            <Tab eventKey='details' title='Custom Details'>
              <Form.Group controlId='new_property_key_contact.name'>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type='text'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.new_property_key_contact.name}
                  placeholder='Name'
                  isInvalid={
                    errors.new_property_key_contact
                      ? errors.new_property_key_contact.name
                      : false
                  }
                />
                <Form.Control.Feedback type='invalid'>
                  {errors.new_property_key_contact
                    ? errors.new_property_key_contact.name
                    : ''}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId='new_property_key_contact.email'>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type='text'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.new_property_key_contact.email}
                  placeholder='Email'
                  isInvalid={
                    errors.new_property_key_contact
                      ? errors.new_property_key_contact.email
                      : false
                  }
                />
                <Form.Control.Feedback type='invalid'>
                  {errors.new_property_key_contact
                    ? errors.new_property_key_contact.email
                    : ''}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId='new_property_key_contact.phone_number'>
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  type='text'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.new_property_key_contact.phone_number}
                  placeholder='Phone Number'
                  isInvalid={
                    errors.new_property_key_contact
                      ? errors.new_property_key_contact.phone_number
                      : false
                  }
                />
                <Form.Control.Feedback type='invalid'>
                  {errors.new_property_key_contact
                    ? errors.new_property_key_contact.phone_number
                    : ''}
                </Form.Control.Feedback>
              </Form.Group>
            </Tab>
          </Tabs>
        </Fragment>
      ) : (
        <Form.Group controlId='property_key_contact_id'>
          <Form.Label>Key Contact</Form.Label>
          <InputGroup>
            <Select
              defaultValue={values.property_key_contact}
              onChange={(option) =>
                setFieldValue('property_key_contact', option)
              }
              options={values.propertyKeyContacts.map((propertyKeyContact) => {
                return {
                  value: propertyKeyContact.id,
                  label: propertyKeyContact.user
                    ? propertyKeyContact.user.name
                    : propertyKeyContact.name
                };
              })}
              styles={{
                container: (styles) => ({ ...styles, flex: '1 1 auto' })
              }}
            />
            <InputGroup.Append style={{ zIndex: 0 }}>
              <Button
                variant='outline-success'
                className='rounded ml-1'
                type='button'
                onClick={() => {
                  setFieldValue('property_key_contact', '');
                  setFieldValue('add_key_contact', true);
                }}
              >
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </InputGroup.Append>
          </InputGroup>
          {errors.property_key_contact ? (
            <div className='text-danger'>{errors.property_key_contact}</div>
          ) : null}
        </Form.Group>
      )}
      {values.add_staff !== undefined && (
        <Form.Group controlId='add_staff'>
          <div>
            <Form.Label className='mr-3'>Give access to all staff?</Form.Label>
            <input
              className='mt-2'
              style={{ width: '16px', height: '16px' }}
              type='checkbox'
              onChange={(e) => {
                setFieldValue('add_staff', e.target.checked);
              }}
              value={values.add_staff}
            />
          </div>
        </Form.Group>
      )}
    </Form>
  );
}

export const PropertyFormValidater = (values, props) => {
  const errors = {};

  const addNewPropertyKeyContactError = (key, msg) => {
    if (!errors.new_property_key_contact) errors.new_property_key_contact = {};
    errors.new_property_key_contact[key] = msg;
  };

  if (!values.identifier) errors.identifier = 'Required';
  else {
    // Check identifier against regext pattern.
    const identifierRegex = /^(?:[RK]\d{4}|\d{5})$/;
    if (!identifierRegex.test(values.identifier))
      errors.identifier = 'Invalid identifier';
  }
  if (!values.name) errors.name = 'Required';
  if (!values.region) errors.region_id = 'Required';
  if (values.add_key_contact) {
    if (values.add_key_contact_tab === 'user') {
      if (!values.new_property_key_contact.user_type)
        addNewPropertyKeyContactError('user_type', 'Required');
      if (!values.new_property_key_contact.user)
        addNewPropertyKeyContactError('user', 'Required');
    } else {
      if (!values.new_property_key_contact.name)
        addNewPropertyKeyContactError('name', 'Required');
      if (!values.new_property_key_contact.email)
        addNewPropertyKeyContactError('email', 'Required');
      if (!values.new_property_key_contact.phone_number)
        addNewPropertyKeyContactError('phone_number', 'Required');
    }
  } else {
    if (!values.property_key_contact) errors.property_key_contact = 'Required';
  }

  return errors;
};

export default PropertyForm;
